import { computeReachablePath, evaluateRoutingPath } from '@/modules/routing'
import type {
  AnswersByQuestionId,
  QuestionAnswer,
  QuestionRoutingRules,
  RoutingQuestionInput,
} from '@/modules/routing'
import { canProceedToNext } from './survey-answer'
import {
  choiceDraftFromLegacy,
  isChoiceDraftAnswer,
  isMultiSelectSurveyQuestion,
} from './choice-answer'
import type { SurveyDefinition, SurveyDraftAnswer, SurveyQuestion } from './survey-question.types'

export function parseRoutingRulesFromDefinition(raw: unknown): QuestionRoutingRules[] {
  if (raw == null || typeof raw !== 'object') return []
  const routingRules = (raw as Record<string, unknown>).routingRules
  if (!Array.isArray(routingRules)) return []
  return routingRules as QuestionRoutingRules[]
}

export function toRoutingQuestions(questions: SurveyQuestion[]): RoutingQuestionInput[] {
  return questions.map((q) => ({
    id: q.id,
    order: q.order,
    type: q.type,
    options: q.options.map((o) => ({ id: o.id, label: o.label })),
    ratingScaleMax: q.ratingScaleMax,
  }))
}

function draftToYesNoRoutingAnswer(draft: SurveyDraftAnswer): QuestionAnswer | undefined {
  if (typeof draft !== 'string') return undefined
  const value = draft === 'yes' || draft === 'no' ? draft : null
  return value ? { kind: 'yes_no', value } : undefined
}

function isSingleChoiceQuestionType(type: SurveyQuestion['type']): boolean {
  return type === 'multiple_choice' || type === 'dropdown' || type === 'picture_choice'
}

function isRatingQuestionType(type: SurveyQuestion['type']): boolean {
  return type === 'rating' || type === 'likert' || type === 'slider'
}

function draftToChoiceRoutingAnswer(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): QuestionAnswer | undefined {
  const optionIds = choiceDraftFromLegacy(question, draft).selectedIds
  const optionId = optionIds[0]
  if (!optionId) return undefined
  return isMultiSelectSurveyQuestion(question)
    ? { kind: 'choice', optionId, optionIds: [...optionIds] }
    : { kind: 'choice', optionId }
}

function draftToRatingRoutingAnswer(draft: SurveyDraftAnswer): QuestionAnswer | undefined {
  if (typeof draft !== 'number') return undefined
  return { kind: 'rating', value: draft }
}

export function draftToRoutingAnswer(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): QuestionAnswer | undefined {
  if (draft == null || isAnswerEmptyForRouting(question, draft)) return undefined

  if (question.type === 'yes_no') {
    return draftToYesNoRoutingAnswer(draft)
  }

  if (isSingleChoiceQuestionType(question.type)) {
    if (typeof draft === 'string' || Array.isArray(draft) || isChoiceDraftAnswer(draft)) {
      return draftToChoiceRoutingAnswer(question, draft)
    }
    return undefined
  }

  if (question.type === 'checkbox') {
    if (Array.isArray(draft) || isChoiceDraftAnswer(draft)) {
      return draftToChoiceRoutingAnswer(question, draft)
    }
    return undefined
  }

  if (isRatingQuestionType(question.type)) {
    return draftToRatingRoutingAnswer(draft)
  }

  return undefined
}

function isAnswerEmptyForRouting(question: SurveyQuestion, draft: SurveyDraftAnswer): boolean {
  return !canProceedToNext(question, draft)
}

export function answersByQuestionIdFromMap(
  definition: SurveyDefinition,
  answerMap: Map<string, SurveyDraftAnswer>,
  currentQuestionId: string,
  currentDraft: SurveyDraftAnswer,
): AnswersByQuestionId {
  const answers: AnswersByQuestionId = {}
  for (const q of definition.questions) {
    const draft = q.id === currentQuestionId ? currentDraft : answerMap.get(q.id) ?? null
    const routingAnswer = draftToRoutingAnswer(q, draft)
    if (routingAnswer !== undefined) {
      answers[q.id] = routingAnswer
    }
  }
  return answers
}

/** Saved answers only — for visit-stack reconciliation after save (no live draft). */
export function answersByQuestionIdFromAnswerMap(
  definition: SurveyDefinition,
  answerMap: Map<string, SurveyDraftAnswer>,
): AnswersByQuestionId {
  const answers: AnswersByQuestionId = {}
  for (const q of definition.questions) {
    const draft = answerMap.get(q.id) ?? null
    const routingAnswer = draftToRoutingAnswer(q, draft)
    if (routingAnswer !== undefined) {
      answers[q.id] = routingAnswer
    }
  }
  return answers
}

/** True when answering `currentDraft` on `currentQuestion` ends the instrument (branching-aware). */
export function willEndInstrumentAfterAnswer(
  definition: SurveyDefinition,
  routingRules: QuestionRoutingRules[],
  answerMap: Map<string, SurveyDraftAnswer>,
  currentQuestion: SurveyQuestion,
  currentDraft: SurveyDraftAnswer,
): boolean {
  const questions = toRoutingQuestions(definition.questions)
  const answers = answersByQuestionIdFromMap(
    definition,
    answerMap,
    currentQuestion.id,
    currentDraft,
  )
  const step = evaluateRoutingPath(
    questions,
    routingRules,
    answers,
    currentQuestion.id,
  )
  return step.endOfInstrument
}

/** Recomputes server-equivalent reachable path for Back stack and progress after save. */
export function reachablePathFromAnswerMap(
  definition: SurveyDefinition,
  routingRules: QuestionRoutingRules[],
  answerMap: Map<string, SurveyDraftAnswer>,
): string[] {
  const questions = toRoutingQuestions(definition.questions)
  const answers: AnswersByQuestionId = {}
  for (const q of definition.questions) {
    const draft = answerMap.get(q.id) ?? null
    const routingAnswer = draftToRoutingAnswer(q, draft)
    if (routingAnswer !== undefined) {
      answers[q.id] = routingAnswer
    }
  }
  return computeReachablePath(questions, routingRules, answers)
}
